import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UserRow } from '@/component/User';
import {
  Close,
  Plus,
  SelectCheckBox,
  VButton,
  VDialog,
  VInput,
  VStack,
} from '@/package';
import { CheckBoxOutlineBlank } from '@/package';
import { VFlex } from '@/package';
import { VChip } from '@/package';
import { cn } from '@/package';
import type { User, UserGroup } from '@/type';

import type { TaskCreateFormType } from './type';

export const TaskCreateFormUser = ({
  isUserAdding,
  setIsUserAdding,
  userList,
  userGroups,
}: {
  isUserAdding: boolean;
  setIsUserAdding: (visible: boolean) => void;
  userList: User[];
  userGroups?: UserGroup[];
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<TaskCreateFormType>();
  const [searchTerm, setSearchTerm] = useState('');
  const [temporalSelectedUserList, setTemporalSelectedUserList] = useState<
    User[]
  >(watch('worker') ?? []);

  const isAllUserSelected = userList.every(user =>
    temporalSelectedUserList.some(w => w.id === user.id),
  );
  const AllMemberIcon = isAllUserSelected
    ? SelectCheckBox
    : CheckBoxOutlineBlank;

  const setTemporalSelectedUser = (worker: User[]) => {
    setTemporalSelectedUserList(worker);
  };

  const toggleAllUser = () => {
    setTemporalSelectedUser(isAllUserSelected ? [] : userList);
  };

  const isUserSelected = (user: User) =>
    temporalSelectedUserList.some(w => w.id === user.id);

  const toggleUser = (user: User) => {
    setTemporalSelectedUser(
      isUserSelected(user)
        ? temporalSelectedUserList.filter(w => w.id !== user.id)
        : [...temporalSelectedUserList, user],
    );
  };

  const onCancel = () => {
    setTemporalSelectedUserList(watch('worker') ?? []);
    setIsUserAdding(false);
  };

  const onConfirm = () => {
    setValue('worker', temporalSelectedUserList, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setIsUserAdding(false);
  };

  return (
    <VDialog
      visible={isUserAdding}
      onUpdateVisible={(visible: boolean) => setIsUserAdding(visible)}
    >
      <VStack className="h-[657px] w-[680px] gap-2 rounded-lg bg-white p-6">
        <VFlex justify="between" items="center">
          <div className={cn('text-headline-md font-bold')}>
            {t('project.labelMember.constructional')}
          </div>
          <Close
            className="h-6 w-6 cursor-pointer"
            onClick={() => setIsUserAdding(false)}
          />
        </VFlex>

        <div className="grid flex-1 grid-cols-2 gap-2">
          <VStack className="flex-1" gap={1.5}>
            <VStack className="flex-1" gap={1}>
              <div>Members</div>
              <VInput
                type="search"
                fluid
                size="md"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <div className="flex-1 overflow-y-auto">
                {!searchTerm && (
                  <VFlex className="p-1" gap={0.5}>
                    <AllMemberIcon
                      className="size-5 "
                      role="button"
                      onClick={toggleAllUser}
                    />
                    <div className="text-body-md text-gray-400">
                      All members
                    </div>
                  </VFlex>
                )}

                {userList
                  .filter(user => user.email.includes(searchTerm))
                  .map(user => {
                    const UserIcon = isUserSelected(user)
                      ? SelectCheckBox
                      : CheckBoxOutlineBlank;

                    return (
                      <VFlex
                        key={user.id}
                        className="p-1"
                        items="center"
                        gap={0.5}
                      >
                        <UserIcon
                          className="size-5"
                          role="button"
                          onClick={() => toggleUser(user)}
                        />
                        <UserRow
                          key={user.id}
                          user={user}
                          userGroups={userGroups}
                        />
                      </VFlex>
                    );
                  })}
              </div>
            </VStack>

            <VButton
              fluid
              outlined
              size="md"
              variant="secondary"
              onClick={onCancel}
            >
              {t('common.cancel.isocinchonine')}
            </VButton>
          </VStack>

          <VStack className="flex-1 " gap={1}>
            <VStack
              className="flex-1 overflow-hidden rounded-md border border-gray-300 p-2"
              gap={1}
            >
              <div className="text-headline-sm text-blue-300">
                Added members
              </div>

              <VFlex className="flex-1 overflow-y-auto">
                <VFlex className="h-fit flex-wrap gap-1">
                  {temporalSelectedUserList.map(user => (
                    <VChip onDelete={() => toggleUser(user)}>
                      {user.email}
                    </VChip>
                  ))}
                </VFlex>
              </VFlex>
            </VStack>

            <VButton variant="primary" size="md" fluid onClick={onConfirm}>
              <Plus className="size-5" />
              <span>Add members</span>
            </VButton>
          </VStack>
        </div>
      </VStack>
    </VDialog>
  );
};
